import React, { useEffect, useState } from "react";
import { InputNumber } from "antd";

/**
 * Helper: parse an ISO-8601 duration (e.g. "P1DT2H30M5S")
 * into an object { days, hours, minutes, seconds }.
 * If any field is missing, it defaults to 0.
 */
function parseIsoDuration(iso: string) {
  const result = { days: 0, hours: 0, minutes: 0, seconds: 0 };
  if (!iso || typeof iso !== "string") {
    return result;
  }

  // Very simple regex to extract numbers before D, H, M, S in an ISO duration.
  // Supports patterns like "P2DT3H4M5S", "PT15M", "P3D", etc.
  const pattern =
    /^P(?:([\d]+)D)?(?:T(?:([\d]+)H)?(?:([\d]+)M)?(?:([\d]+)S)?)?$/;
  const matches = iso.match(pattern);
  if (!matches) {
    return result;
  }

  // matches[1] = days, matches[2] = hours, matches[3] = minutes, matches[4] = seconds
  if (matches[1]) {
    result.days = parseInt(matches[1], 10);
  }
  if (matches[2]) {
    result.hours = parseInt(matches[2], 10);
  }
  if (matches[3]) {
    result.minutes = parseInt(matches[3], 10);
  }
  if (matches[4]) {
    result.seconds = parseInt(matches[4], 10);
  }

  return result;
}

/**
 * Helper: build an ISO-8601 duration string from { days, hours, minutes, seconds }.
 * If all values are zero, returns an empty string (so you can force a validation error).
 * Otherwise, returns e.g. "P1DT2H30M5S" or "PT45M" or "P2D".
 */
function buildIsoDuration({
  days,
  hours,
  minutes,
  seconds,
}: {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}) {
  // If everything is 0 or null/undefined, return empty.
  if (!days && !hours && !minutes && !seconds) {
    return "";
  }

  let str = "P";
  if (days) {
    str += `${days}D`;
  }
  if (hours || minutes || seconds) {
    str += "T";
    if (hours) {
      str += `${hours}H`;
    }
    if (minutes) {
      str += `${minutes}M`;
    }
    if (seconds) {
      str += `${seconds}S`;
    }
  }
  return str;
}

type DurationPickerProps = {
  value?: string;
  onChange?: (value: string) => void;
};

export const DurationPicker: React.FC<DurationPickerProps> = ({
  value = "",
  onChange,
}) => {
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const parsed = parseIsoDuration(value);
    setDays(parsed.days);
    setHours(parsed.hours);
    setMinutes(parsed.minutes);
    setSeconds(parsed.seconds);
  }, [value]);

  // Handler: whenever any single field changes, update local state,
  // recompute ISO string, and call onChange.
  const triggerChange = (
    newFields: Partial<{
      days: number;
      hours: number;
      minutes: number;
      seconds: number;
    }>,
  ) => {
    const newDays = newFields.days !== undefined ? newFields.days : days;
    const newHours = newFields.hours !== undefined ? newFields.hours : hours;
    const newMinutes =
      newFields.minutes !== undefined ? newFields.minutes : minutes;
    const newSeconds =
      newFields.seconds !== undefined ? newFields.seconds : seconds;

    const isoString = buildIsoDuration({
      days: newDays,
      hours: newHours,
      minutes: newMinutes,
      seconds: newSeconds,
    });

    // Send the new ISO string back up to Form
    onChange?.(isoString);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <div className="flex flex-col">
        <label className="text-sm font-medium">Days</label>
        <InputNumber
          className="w-24"
          min={0}
          value={days}
          onChange={(val) => {
            const v = val || 0;
            setDays(v);
            triggerChange({ days: v });
          }}
        />
      </div>

      <div className="flex flex-col">
        <label className="text-sm font-medium">Hours</label>
        <InputNumber
          className="w-24"
          min={0}
          max={23}
          value={hours}
          onChange={(val) => {
            const v = val || 0;
            setHours(v);
            triggerChange({ hours: v });
          }}
        />
      </div>

      <div className="flex flex-col">
        <label className="text-sm font-medium">Minutes</label>
        <InputNumber
          className="w-24"
          min={0}
          max={59}
          value={minutes}
          onChange={(val) => {
            const v = val || 0;
            setMinutes(v);
            triggerChange({ minutes: v });
          }}
        />
      </div>

      <div className="flex flex-col">
        <label className="text-sm font-medium">Seconds</label>
        <InputNumber
          className="w-24"
          min={0}
          max={59}
          value={seconds}
          onChange={(val) => {
            const v = val || 0;
            setSeconds(v);
            triggerChange({ seconds: v });
          }}
        />
      </div>
    </div>
  );
};
